// Home.jsx
import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  FaRocket,
  FaLightbulb,
  FaPalette,
  FaCode,
  FaBrain,
  FaMobile,
  FaDesktop,
  FaChartLine,
  FaCheckCircle,
  FaArrowRight,
} from "react-icons/fa";
import { HiSparkles } from "react-icons/hi";
import Hero from "../assets/Rectangle-3-1.png";
import Hero1 from "../assets/Rectangle-39.png";
import { useNavigate } from "react-router-dom";

// Hero Section
function HeroSection() {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 500], [0, 200]);

  return (
    <section className="relative h-screen w-full overflow-hidden bg-black">
      {/* Background Image with Parallax */}
      <motion.div style={{ y: y1 }} className="absolute inset-0 z-0">
        <img
          src={Hero}
          alt="Hero Background"
          className="h-full w-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black"></div>
      </motion.div>

      {/* Animated Gradient Orbs */}
      <div className="absolute inset-0 z-10 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, -100, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute top-1/4 left-1/4 h-96 w-96 rounded-full bg-cyan-500/30 blur-[120px]"
        />
        <motion.div
          animate={{
            x: [0, -100, 0],
            y: [0, 100, 0],
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute bottom-1/4 right-1/4 h-96 w-96 rounded-full bg-purple-500/30 blur-[120px]"
        />
      </div>

      {/* Main Content */}
      <div className="relative z-20 flex h-full flex-col items-center justify-center px-4 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="max-w-5xl"
        >
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-6 bg-gradient-to-b from-white via-white to-gray-300 bg-clip-text text-5xl font-extrabold tracking-tighter text-transparent sm:text-6xl md:text-6xl lg:text-7xl drop-shadow-lg"
          >
            Transforming Vision Into Reality
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="mx-auto mb-10 max-w-2xl text-lg font-light text-white md:text-xl leading-relaxed drop-shadow-md"
          >
            Nexonetics leverages cutting-edge software development to turn your
            visionary ideas into powerful, tangible digital solutions.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center"
          >
            <button
              onClick={() => navigate("/contact")}
              className="group relative flex items-center gap-2 overflow-hidden rounded-full bg-white px-8 py-4 text-lg font-bold text-black transition-all hover:bg-cyan-50 hover:scale-105 shadow-lg"
            >
              <span>Get Started</span>
              <FaArrowRight className="transition-transform group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => navigate("/portfolio")}
              className="group rounded-full border border-white/30 bg-white/10 backdrop-blur-md px-8 py-4 text-lg font-medium text-white transition-all hover:bg-white/20 hover:border-white shadow-lg"
            >
              View Our Work
            </button>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Hint */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30">
        <motion.div
          animate={{ y: [0, 10, 0], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex flex-col items-center gap-2 text-white/50"
        >
          <span className="text-xs uppercase tracking-widest">Scroll</span>
          <div className="h-10 w-[1px] bg-gradient-to-b from-white to-transparent"></div>
        </motion.div>
      </div>
    </section>
  );
}

// Features Section - Bento Grid
function FeaturesSection() {
  const features = [
    {
      icon: FaLightbulb,
      title: "Innovative Solutions",
      desc: "Stay ahead of the curve with our innovative solutions. We leverage the latest technologies and creative strategies to solve complex problems and drive business growth.",
      colSpan: "md:col-span-2",
      bg: "bg-gradient-to-br from-gray-900 to-black",
    },
    {
      icon: HiSparkles,
      title: "Personalized Experiences",
      desc: "Constantly adopting cutting edge technology for your enterprise to harness its endless possibilities.",
      colSpan: "md:col-span-1",
      bg: "bg-black",
    },
    {
      icon: FaPalette,
      title: "Unobtrusive Design",
      desc: "Technology that complements your personal and professional spaces without being intrusive. Our designs are sleek, minimal, and blend perfectly.",
      colSpan: "md:col-span-3",
      bg: "bg-gradient-to-r from-gray-900 via-black to-gray-900",
    },
  ];

  return (
    <section className="bg-black py-24 px-4">
      <div className="mx-auto max-w-7xl">
        <div className="mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-white md:text-6xl"
          >
            Why <span className="text-gray-500">Nexonetics?</span>
          </motion.h2>
          <div className="mt-4 h-[1px] w-full max-w-xs bg-gradient-to-r from-cyan-500 to-transparent"></div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {features.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.2 }}
              viewport={{ once: true }}
              className={`${f.colSpan} group relative overflow-hidden rounded-3xl border border-white/10 ${f.bg} p-8 hover:border-white/30 transition-colors duration-500`}
            >
              <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 text-2xl text-white backdrop-blur-sm transition-transform group-hover:scale-110 group-hover:bg-white group-hover:text-black">
                <f.icon />
              </div>
              <h3 className="mb-3 text-2xl font-bold text-white">{f.title}</h3>
              <p className="text-gray-400 group-hover:text-gray-200 transition-colors">
                {f.desc}
              </p>

              {/* Hover Glow Effect */}
              <div className="absolute -right-20 -bottom-20 h-64 w-64 rounded-full bg-cyan-500/20 blur-3xl transition-opacity opacity-0 group-hover:opacity-100"></div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Services Section
function ServicesSection() {
  const services = [
    {
      icon: FaCode,
      title: "Software Development",
      tags: ["Web", "Mobile", "Desktop"],
    },
    {
      icon: FaBrain,
      title: "AI & Machine Learning",
      tags: ["Automation", "Insights"],
    },
    {
      icon: FaPalette,
      title: "UI/UX Design",
      tags: ["User Interface", "Brand Identity"],
    },
    { icon: FaChartLine, title: "Digital Marketing", tags: ["SEO", "Growth"] },
    {
      icon: FaDesktop,
      title: "Enterprise Resource Planning",
      tags: ["Enterprise", "Cloud"],
    },
    {
      icon: FaCheckCircle,
      title: "End to End Testing",
      tags: ["QA", "Security"],
    },
  ];

  return (
    <section className="bg-white py-24 px-4 text-black">
      <div className="mx-auto max-w-7xl">
        <div className="mb-20 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-5xl font-bold tracking-tight md:text-7xl"
          >
            Our Expertise
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-6 text-xl text-gray-500"
          >
            Comprehensive digital solutions engineered for growth.
          </motion.p>
        </div>

        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
              className="group cursor-pointer border-t border-gray-200 pt-8 transition-colors hover:border-black"
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-2xl font-semibold">{s.title}</h3>
                <s.icon className="text-2xl text-gray-400 transition-colors group-hover:text-black" />
              </div>
              <div className="flex flex-wrap gap-2">
                {s.tags.map((tag, t) => (
                  <span
                    key={t}
                    className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 transition-colors group-hover:bg-black group-hover:text-white"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Main Home Component
function Home() {
  return (
    <div className="bg-black text-white overflow-x-hidden">
      <HeroSection />
      <FeaturesSection />
      <ServicesSection />
      {/* <GallerySection /> */}
    </div>
  );
}

export default Home;
